import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { liberacaoService } from '../services';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import logoImage from '../assets/images/logo_farmacia.png';
import { 
  ArrowLeft, 
  Download, 
  AlertTriangle,
  FileText
} from 'lucide-react';

const ReciboLiberacao = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const reciboRef = useRef();
  const [liberacao, setLiberacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);
        const data = await liberacaoService.getById(id);
        
        if (data.status !== 'confirmada') {
          setMessage({ 
            type: 'error', 
            text: 'Recibo só pode ser gerado para liberações confirmadas' 
          });
          return;
        }
        
        setLiberacao(data);
      } catch (error) {
        console.error('Erro ao carregar liberação:', error);
        setMessage({ type: 'error', text: 'Erro ao carregar dados da liberação' });
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [id]);



  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarDataHora = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR');
  };

  const formatarCpf = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatarCartaoSus = (cartao) => {
    if (!cartao) return '-';
    return cartao.replace(/(\d{3})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
  };

  const gerarPDF = useCallback(async () => {
    if (!liberacao) return;
    
    try {
      setGerando(true);
      
      const elemento = reciboRef.current;
      const canvas = await html2canvas(elemento, {
        scale: 2,
        useCORS: true,
        allowTaint: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const nomeArquivo = `recibo-${liberacao.numeroRecibo}.pdf`;
      pdf.save(nomeArquivo);
      
      setMessage({ 
        type: 'success', 
        text: `Recibo "${nomeArquivo}" baixado com sucesso!` 
      });
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setMessage({ type: 'error', text: 'Erro ao gerar o PDF do recibo' });
    } finally {
      setGerando(false);
    }
  }, [liberacao]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!liberacao) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <AlertTriangle size={48} color="#ff6b6b" style={{ marginBottom: '15px' }} />
          <h3>Liberação não encontrada</h3>
          <p style={{ color: '#667', marginBottom: '20px' }}>
            A liberação solicitada não existe ou não pode ser impressa.
          </p>
          <button 
            onClick={() => navigate('/liberacoes')}
            className="btn btn-primary"
          >
            <ArrowLeft size={20} />
            Voltar para Liberações
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={32} />
            Recibo de Liberação
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            Nº {liberacao.numeroRecibo}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={gerarPDF}
            className="btn btn-primary"
            disabled={gerando}
          >
            {gerando ? (
              <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
            ) : (
              <>
                <Download size={20} />
                Baixar PDF
              </>
            )}
          </button>
          <button 
            onClick={() => navigate(`/liberacoes/detalhes/${liberacao.id}`)}
            className="btn btn-secondary"
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      {/* Recibo para impressão */}
      <div 
        ref={reciboRef}
        style={{
          backgroundColor: 'white',
          padding: '40px',
          margin: '0 auto',
          maxWidth: '800px',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          fontFamily: 'Arial, sans-serif',
          fontSize: '14px',
          lineHeight: '1.6'
        }}
      >
        {/* Cabeçalho */}
        <div style={{ 
          borderBottom: '3px solid #007bff',
          paddingBottom: '20px',
          marginBottom: '30px'
        }}>
          {/* Logo e Nome da Farmácia */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            marginBottom: '20px',
            gap: '20px'
          }}>
            {/* Logo */}
            <div>
              <img 
                src={logoImage} 
                alt="Farmácia Popular de Minas" 
                style={{ 
                  width: '72px', 
                  height: 'auto',
                  maxHeight: '72px',
                  objectFit: 'contain'
                }}
                onError={(e) => {e.target.style.display = 'none'}}
              />
            </div>
            
            {/* Texto da Farmácia */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 'bold',
                color: '#333',
                lineHeight: '1.2'
              }}>
                Farmácia Popular
              </div>
              <div style={{ 
                fontSize: '26px', 
                fontWeight: 'bold',
                color: '#333',
                lineHeight: '1.2'
              }}>
                de Minas
              </div>
            </div>
          </div>
          
          {/* Título */}
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ 
              color: '#333', 
              fontSize: '24px', 
              margin: '0 0 15px 0',
              fontWeight: 'bold'
            }}>
              Recibo de Medicamentos
            </h2>
            
            {/* Declaração */}
            <p style={{ 
              color: '#555', 
              fontSize: '16px', 
              margin: '0',
              fontWeight: 'normal',
              lineHeight: '1.4'
            }}>
              Declaro que recebi, da farmácia de Minas, o(s) seguinte(s) medicamento(s) referentes ao processo: {liberacao?.numeroProcesso && <strong>{liberacao.numeroProcesso}</strong>}
            </p>
          </div>
        </div>

        {/* Informações do Recibo */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr',
          gap: '30px',
          marginBottom: '30px'
        }}>
          <div>
            <h3 style={{ 
              color: '#007bff', 
              fontSize: '16px', 
              marginBottom: '15px',
              borderBottom: '1px solid #ddd',
              paddingBottom: '5px'
            }}>
              DADOS DA LIBERAÇÃO
            </h3>
            <p><strong>Número do Recibo:</strong> {liberacao.numeroRecibo}</p>
            <p><strong>Data/Hora:</strong> {formatarDataHora(liberacao.dataLiberacao)}</p>
            <p><strong>Responsável:</strong> {liberacao.usuario?.nome}</p>
            <p><strong>Cargo:</strong> {liberacao.usuario?.cargo}</p>
            <p><strong>Total de Itens:</strong> {liberacao.totalItens}</p>
          </div>

          <div>
            <h3 style={{ 
              color: '#007bff', 
              fontSize: '16px', 
              marginBottom: '15px',
              borderBottom: '1px solid #ddd',
              paddingBottom: '5px'
            }}>
              DADOS DO PACIENTE
            </h3>
            <p><strong>Nome:</strong> {liberacao.paciente?.nome}</p>
            <p><strong>CPF:</strong> {formatarCpf(liberacao.paciente?.cpf)}</p>
            <p><strong>RG:</strong> {liberacao.paciente?.rg || 'Não informado'}</p>
            <p><strong>Cartão SUS:</strong> {formatarCartaoSus(liberacao.paciente?.cartaoSus)}</p>
            <p><strong>Endereço:</strong> {liberacao.paciente?.endereco || 'Não informado'}</p>
          </div>
        </div>

        {/* Observações da Liberação */}
        {liberacao.observacoes && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ 
              color: '#007bff', 
              fontSize: '16px', 
              marginBottom: '10px',
              borderBottom: '1px solid #ddd',
              paddingBottom: '5px'
            }}>
              OBSERVAÇÕES DA LIBERAÇÃO
            </h3>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '5px',
              border: '1px solid #ddd',
              whiteSpace: 'pre-wrap'
            }}>
              {liberacao.observacoes}
            </div>
          </div>
        )}

        {/* Medicamentos */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ 
            color: '#007bff', 
            fontSize: '16px', 
            marginBottom: '15px',
            borderBottom: '1px solid #ddd',
            paddingBottom: '5px'
          }}>
            MEDICAMENTOS LIBERADOS
          </h3>
          
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            border: '1px solid #ddd'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px', 
                  textAlign: 'left',
                  fontWeight: 'bold'
                }}>
                  Medicamento
                </th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px', 
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  Qtd
                </th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px', 
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  Lote
                </th>
                <th style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px', 
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  Validade
                </th>
              </tr>
            </thead>
            <tbody>
              {liberacao.itens?.map((item, index) => (
                <tr key={item.id} style={{ 
                  backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9' 
                }}>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '10px'
                  }}>
                    <div>
                      <strong>{item.nomeMedicamento}</strong>
                      {item.principioAtivo && (
                        <div style={{ fontSize: '12px', color: '#667' }}>
                          Princípio: {item.principioAtivo}
                        </div>
                      )}
                      {item.observacoes && (
                        <div style={{ fontSize: '12px', color: '#667', marginTop: '5px' }}>
                          Obs: {item.observacoes}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '10px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    {item.quantidade}
                  </td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '10px',
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }}>
                    {item.lote || '-'}
                  </td>
                  <td style={{ 
                    border: '1px solid #ddd', 
                    padding: '10px',
                    textAlign: 'center'
                  }}>
                    {item.dataValidade ? formatarData(item.dataValidade) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Assinaturas */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr',
          gap: '50px',
          marginTop: '50px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              borderTop: '1px solid #333', 
              paddingTop: '10px',
              marginBottom: '5px'
            }}>
              <strong>{liberacao.usuario?.nome}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#667' }}>
              {liberacao.usuario?.cargo} - Responsável pela Liberação
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              borderTop: '1px solid #333', 
              paddingTop: '10px',
              marginBottom: '5px'
            }}>
              <strong>{liberacao.paciente?.nome}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#667' }}>
              Paciente - Assinatura do Recebedor
            </div>
          </div>
        </div>

        {/* Rodapé */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid #ddd',
          fontSize: '12px',
          color: '#667'
        }}>
          <p>
            Este recibo comprova a liberação dos medicamentos acima listados.
          </p>
          <p>
            Documento gerado em {new Date().toLocaleString('pt-BR')} pela Farmácia Popular de Minas.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReciboLiberacao;